import { User, UserLoginDetailTypeEnum } from '@jooxter/api';
import { getWindowName, createGTMUpdateVirtualPathEvent } from '@jooxter/utils';
import { useRef, useEffect } from 'react';
import TagManager from 'react-gtm-module';
import { GTM_VIEW_PAGE_MAPPING } from '../gtmViewPageMapping';
import { getEnv } from '@jooxter/env';
import { useFetchLoginDetails } from '@jooxter/core';

const cleanGTMOnLogout = () => {
  TagManager.dataLayer({
    dataLayer: {
      userId: null,
      connectionType: null,
    },
  });
};

export const useGTM = (user?: User, pathname?: string) => {
  // Acts as a guard for gtm initialization
  const isGTMInitialized = useRef<boolean>(false);
  const { GTM_ID } = getEnv();
  const { loginDetails } = useFetchLoginDetails(user?.email);

  // Initialize once and push static dimensions
  useEffect(() => {
    if (GTM_ID && !isGTMInitialized.current) {
      TagManager.initialize({ gtmId: GTM_ID });
      isGTMInitialized.current = true;

      TagManager.dataLayer({
        dataLayer: {
          clientName: getWindowName(),
        },
      });
    }
  }, [GTM_ID]);

  // Push user dimension on change
  useEffect(() => {
    if (!isGTMInitialized.current || !user) {
      return;
    }

    TagManager.dataLayer({
      dataLayer: {
        userId: user.id,
      },
    });

    return () => cleanGTMOnLogout();
  }, [user]);

  // Push connection type dimension on change
  useEffect(() => {
    if (!isGTMInitialized.current || !loginDetails) {
      return;
    }

    const connectionType =
      loginDetails.type === UserLoginDetailTypeEnum.Saml2 || loginDetails.type === UserLoginDetailTypeEnum.Oidc
        ? 'SSO'
        : 'JOOXTER';

    TagManager.dataLayer({
      dataLayer: {
        connectionType,
      },
    });

    return () => cleanGTMOnLogout();
  }, [loginDetails]);

  // Push virtual page views on route change — only after GTM is ready
  useEffect(() => {
    if (isGTMInitialized.current && pathname && pathname in GTM_VIEW_PAGE_MAPPING) {
      createGTMUpdateVirtualPathEvent(GTM_VIEW_PAGE_MAPPING[pathname]);
    }
  }, [pathname]);
};
